import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { prefsAPI, ebayAPI } from "@/lib/api";
import {
  SignOut,
  User,
  Check,
  Plus,
  Trash,
  ArrowCounterClockwise,
  ArrowRight,
  CheckCircle,
  Storefront,
  MapPin,
  Car,
  Megaphone,
  PencilSimple,
  Star,
  Pause,
  Play,
  ChartLine,
  Coins,
  TShirt,
} from "@phosphor-icons/react";
import BottomNav from "@/components/app/BottomNav";
import { useConfirm } from "@/components/app/ConfirmDialog";

const ALL_PLATFORMS = ["eBay", "Poshmark", "Mercari", "Depop", "Facebook Marketplace"];

export function SectionHeader({ title }) {
  return (
    <div className="font-heading text-lg font-bold tracking-tight uppercase border-b-2 border-black pb-2 mb-4">
      {title}
    </div>
  );
}

function PlatformToggle({ platform, selected, onToggle }) {
  return (
    <button
      onClick={() => onToggle(platform)}
      className={`px-3 py-2 border-2 border-black font-body text-sm font-semibold transition-all ${
        selected
          ? "bg-black text-white shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
          : "bg-white text-black hover:bg-neutral-100"
      }`}
      data-testid={`platform-toggle-${platform.toLowerCase().replace(/\s/g, '-')}`}
    >
      {platform}
    </button>
  );
}

function BrandList({ title, brands, onAdd, onRemove, testidPrefix }) {
  const [input, setInput] = useState("");

  const handleAdd = () => {
    const brand = input.trim();
    if (brand && !brands.some((b) => b.toLowerCase() === brand.toLowerCase())) {
      onAdd(brand);
      setInput("");
    }
  };

  return (
    <div>
      <div className="font-body text-xs font-semibold tracking-[0.1em] uppercase text-neutral-500 mb-2">
        {title}
      </div>
      <div className="flex gap-2 mb-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          placeholder="Add brand..."
          className="flex-1 border-2 border-black bg-white px-3 py-2 font-body text-sm focus:outline-none focus:ring-4 focus:ring-black/20"
          data-testid={`${testidPrefix}-input`}
        />
        <button
          onClick={handleAdd}
          className="px-3 border-2 border-black bg-black text-white font-bold"
          data-testid={`${testidPrefix}-add-btn`}
        >
          <Plus size={16} weight="bold" />
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {brands.map((brand) => (
          <div
            key={brand}
            className="flex items-center gap-1 px-2 py-1 border border-black bg-neutral-50 font-body text-xs"
          >
            <span>{brand}</span>
            <button
              onClick={() => onRemove(brand)}
              className="text-neutral-400 hover:text-[#FF1744]"
            >
              <Trash size={10} weight="bold" />
            </button>
          </div>
        ))}
        {brands.length === 0 && (
          <span className="font-body text-xs text-neutral-400">No brands added</span>
        )}
      </div>
    </div>
  );
}

export default function ProfilePage() {
  const { user, logout } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSetup = searchParams.get("setup") === "1";

  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [ebayStatus, setEbayStatus] = useState(null);
  const [ebayPolicies, setEbayPolicies] = useState(null);

  const loadPrefs = useCallback(async () => {
    try {
      const [prefsRes, ebayRes] = await Promise.all([
        prefsAPI.get(),
        ebayAPI.authStatus().catch(() => ({ data: { connected: false } })),
      ]);
      setPrefs(prefsRes.data);
      setEbayStatus(ebayRes.data);
      // Load policies if connected
      if (ebayRes.data?.connected) {
        ebayAPI.policies().then(({ data }) => setEbayPolicies(data)).catch(() => {});
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPrefs();
  }, [loadPrefs]);

  const savePrefs = async (updates) => {
    setSaving(true);
    setSaved(false);
    try {
      const { data } = await prefsAPI.update(updates);
      setPrefs(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const togglePlatform = (platform) => {
    if (!prefs) return;
    const current = prefs.platforms || [];
    let updated;
    if (current.includes(platform)) {
      updated = current.filter((p) => p !== platform);
      if (updated.length === 0) return; // must have at least one
    } else {
      updated = [...current, platform];
    }
    setPrefs({ ...prefs, platforms: updated });
    savePrefs({ platforms: updated });
  };

  const addGrabBrand = (brand) => {
    if (!prefs) return;
    const updated = [...(prefs.always_grab_brands || []), brand];
    setPrefs({ ...prefs, always_grab_brands: updated });
    savePrefs({ always_grab_brands: updated });
  };

  const removeGrabBrand = (brand) => {
    if (!prefs) return;
    const updated = (prefs.always_grab_brands || []).filter((b) => b !== brand);
    setPrefs({ ...prefs, always_grab_brands: updated });
    savePrefs({ always_grab_brands: updated });
  };

  const addSkipBrand = (brand) => {
    if (!prefs) return;
    const updated = [...(prefs.skip_brands || []), brand];
    setPrefs({ ...prefs, skip_brands: updated });
    savePrefs({ skip_brands: updated });
  };

  const removeSkipBrand = (brand) => {
    if (!prefs) return;
    const updated = (prefs.skip_brands || []).filter((b) => b !== brand);
    setPrefs({ ...prefs, skip_brands: updated });
    savePrefs({ skip_brands: updated });
  };

  const handleResetBrandsAndPrices = async () => {
    const ok = await confirm({
      title: "Reset to defaults?",
      message: "Your brand rules and price targets will return to defaults. Your eBay settings won't change.",
      confirmLabel: "Reset",
      cancelLabel: "Keep mine",
      destructive: true,
    });
    if (!ok) return;
    try {
      const { data } = await prefsAPI.resetBrandRulesAndPrices();
      setPrefs(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // ignore
    }
  };

  const handleReset = async () => {
    try {
      const { data } = await prefsAPI.reset();
      setPrefs(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // ignore
    }
  };

  const handleSetupDone = async () => {
    await savePrefs({ setup_complete: true });
    navigate("/scan?welcome=1");
  };

  const handleConnectEbay = async () => {
    try {
      const { data } = await ebayAPI.connect();
      if (data.auth_url) {
        window.location.href = data.auth_url;
      }
    } catch {
      // ignore
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pb-24">
        <div className="p-8 text-center font-body text-neutral-500">Loading...</div>
        <BottomNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-24">
      <div className="sticky top-0 z-50 bg-white border-b-4 border-black py-4 px-6 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-black tracking-tighter uppercase">
          Profile
        </h1>
        {saved && (
          <div className="flex items-center gap-1 text-green-600">
            <Check size={16} weight="bold" />
            <span className="font-body text-xs font-bold">Saved</span>
          </div>
        )}
      </div>

      <div className="p-6 space-y-7 max-w-2xl mx-auto">
        {/* User Info */}
        <div className="border-4 border-black p-5 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]" data-testid="profile-card">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 border-2 border-black bg-neutral-100 flex items-center justify-center">
              <User size={28} weight="bold" className="text-neutral-400" />
            </div>
            <div>
              <div className="font-heading text-xl font-bold tracking-tight">{user?.name}</div>
              <div className="font-body text-sm text-neutral-500">{user?.email}</div>
            </div>
          </div>
        </div>

        {/* Ledger link */}
        <button
          onClick={() => navigate("/ledger")}
          className="w-full border-4 border-black bg-white p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:bg-neutral-50 flex items-center justify-between"
          data-testid="profile-ledger-link"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border-2 border-black bg-black text-white flex items-center justify-center">
              <Coins size={20} weight="bold" />
            </div>
            <div className="text-left">
              <div className="font-heading text-base font-bold uppercase tracking-tight">Ledger</div>
              <div className="font-body text-xs text-neutral-500">Track deposits, debits & balance</div>
            </div>
          </div>
          <ArrowRight size={20} weight="bold" />
        </button>

        {/* Size Check link */}
        <button
          onClick={() => navigate("/size-check")}
          className="w-full border-4 border-black bg-white p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:bg-neutral-50 flex items-center justify-between"
          data-testid="profile-size-check-link"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border-2 border-black bg-black text-white flex items-center justify-center">
              <TShirt size={20} weight="bold" />
            </div>
            <div className="text-left">
              <div className="font-heading text-base font-bold uppercase tracking-tight">Size Check</div>
              <div className="font-body text-xs text-neutral-500">Find & fix non-standard sizes on live listings</div>
            </div>
          </div>
          <ArrowRight size={20} weight="bold" />
        </button>


        {/* Brand Rules */}
        <div>
          <SectionHeader title="Brand Rules" />
          <div className="space-y-5">
            <BrandList
              title="Always Grab (auto-upgrade to GRAB)"
              brands={prefs?.always_grab_brands || []}
              onAdd={addGrabBrand}
              onRemove={removeGrabBrand}
              testidPrefix="grab-brand"
            />
            <BrandList
              title="Always Skip (auto-downgrade to SKIP)"
              brands={prefs?.skip_brands || []}
              onAdd={addSkipBrand}
              onRemove={removeSkipBrand}
              testidPrefix="skip-brand"
            />
          </div>
        </div>

        {/* Price Targets */}
        <div>
          <SectionHeader title="Price Targets" />
          <div className="space-y-3">
            <div>
              <label className="block font-body text-xs font-semibold tracking-[0.1em] uppercase mb-1.5">
                Auto-Skip Below (minimum price to consider)
              </label>
              <div className="flex items-center gap-2">
                <span className="font-heading text-lg font-bold">$</span>
                <input
                  type="number"
                  value={prefs?.min_list_price || 0}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    setPrefs({ ...prefs, min_list_price: val });
                  }}
                  onBlur={() => savePrefs({ min_list_price: prefs?.min_list_price || 0 })}
                  min="0"
                  step="1"
                  className="w-24 border-2 border-black bg-white px-3 py-2 font-heading text-lg focus:outline-none focus:ring-4 focus:ring-black/20"
                  data-testid="min-price-input"
                />
                <span className="font-body text-xs text-neutral-500">Set to $0 to disable</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-body text-xs font-semibold tracking-[0.1em] uppercase mb-1.5">
                  Sweet Spot Low
                </label>
                <div className="flex items-center gap-1">
                  <span className="font-heading font-bold">$</span>
                  <input
                    type="number"
                    value={prefs?.target_price_low || 20}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      setPrefs({ ...prefs, target_price_low: val });
                    }}
                    onBlur={() => savePrefs({ target_price_low: prefs?.target_price_low || 20 })}
                    min="0"
                    className="w-20 border-2 border-black bg-white px-3 py-2 font-heading text-lg focus:outline-none focus:ring-4 focus:ring-black/20"
                    data-testid="target-low-input"
                  />
                </div>
              </div>
              <div>
                <label className="block font-body text-xs font-semibold tracking-[0.1em] uppercase mb-1.5">
                  Sweet Spot High
                </label>
                <div className="flex items-center gap-1">
                  <span className="font-heading font-bold">$</span>
                  <input
                    type="number"
                    value={prefs?.target_price_high || 30}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value) || 0;
                      setPrefs({ ...prefs, target_price_high: val });
                    }}
                    onBlur={() => savePrefs({ target_price_high: prefs?.target_price_high || 30 })}
                    min="0"
                    className="w-20 border-2 border-black bg-white px-3 py-2 font-heading text-lg focus:outline-none focus:ring-4 focus:ring-black/20"
                    data-testid="target-high-input"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Connect eBay link */}
        <button
          onClick={() => navigate("/connect-ebay")}
          className="w-full border-2 border-black bg-white p-4 flex items-center justify-between hover:bg-neutral-50 active:translate-y-[1px] transition-all"
          data-testid="profile-connect-ebay-link"
        >
          <div className="flex items-center gap-3">
            <Storefront size={20} weight="bold" />
            <div className="text-left">
              <div className="font-heading text-sm font-bold uppercase tracking-wide">Connect eBay</div>
              <div className="font-body text-[11px] text-neutral-500">
                {ebayStatus?.connected ? "Connected · Manage defaults" : "Not connected · Tap to connect"}
              </div>
            </div>
          </div>
          <ArrowRight size={18} weight="bold" className="text-neutral-400" />
        </button>

        {/* Super admin: Usage & Cost — only visible to super_admin role */}
        {user?.role === "super_admin" && (
          <button
            onClick={() => navigate("/admin/usage")}
            className="w-full border-2 border-black bg-black text-white p-4 flex items-center justify-between hover:bg-neutral-900 active:translate-y-[1px] transition-all"
            data-testid="profile-admin-usage-link"
          >
            <div className="flex items-center gap-3">
              <ChartLine size={20} weight="bold" />
              <div className="text-left">
                <div className="font-heading text-sm font-bold uppercase tracking-wide">API Usage & Cost</div>
                <div className="font-body text-[11px] text-neutral-400">
                  Super admin · LLM tokens, SerpApi calls, per-user cost
                </div>
              </div>
            </div>
            <ArrowRight size={18} weight="bold" className="text-neutral-400" />
          </button>
        )}

        {/* Reset brand rules and price targets to default */}
        <button
          onClick={handleResetBrandsAndPrices}
          className="w-full p-3 border-2 border-black flex items-center justify-center gap-2 font-body font-semibold text-sm uppercase tracking-[0.1em] hover:bg-neutral-100"
          data-testid="reset-prefs-btn"
        >
          <ArrowCounterClockwise size={16} weight="bold" />
          Reset brand rules and price targets to default
        </button>

        {/* Sign Out */}
        <button
          onClick={handleLogout}
          className="w-full bg-[#FF1744] text-white p-4 font-heading font-bold text-lg uppercase tracking-wide border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:translate-y-[2px] active:shadow-none transition-all flex items-center justify-center gap-3"
          data-testid="logout-btn"
        >
          <SignOut size={24} weight="bold" />
          Sign Out
        </button>
      </div>

      <BottomNav />
    </div>
  );
}



export function CampaignManager() {
  const confirm = useConfirm();
  const [campaigns, setCampaigns] = useState([]);
  const [defaultId, setDefaultId] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newRate, setNewRate] = useState("5");
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editRate, setEditRate] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const loadCampaigns = useCallback(async () => {
    try {
      const { data } = await ebayAPI.campaigns();
      setCampaigns(data.campaigns || []);
      setDefaultId(data.default_campaign_id || "");
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setErrorMsg("");
    try {
      await ebayAPI.createCampaign({ name: newName, ad_rate: parseFloat(newRate) || 5 });
      setNewName("");
      setNewRate("5");
      await loadCampaigns();
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || "Failed to create campaign");
    }
    setCreating(false);
  };

  const handleSuggest = async () => {
    setCreating(true);
    try {
      await ebayAPI.suggestCampaigns();
      await loadCampaigns();
    } catch {}
    setCreating(false);
  };

  const handleSetDefault = async (id) => {
    try {
      await ebayAPI.setDefaultCampaign(id);
      setDefaultId(id);
    } catch {}
  };

  const handleUpdate = async (id) => {
    setErrorMsg("");
    try {
      await ebayAPI.updateCampaign(id, {
        name: editName || undefined,
        ad_rate: editRate ? parseFloat(editRate) : undefined,
      });
      setEditingId(null);
      await loadCampaigns();
    } catch (err) {
      setErrorMsg(err.response?.data?.detail || "Failed to update campaign");
    }
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Delete this campaign?",
      message: "Items currently in this campaign will be unpromoted.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await ebayAPI.deleteCampaign(id);
      await loadCampaigns();
    } catch {}
  };

  const handlePauseResume = async (campaign) => {
    try {
      if (campaign.status === "RUNNING") {
        await ebayAPI.pauseCampaign(campaign.campaign_id);
      } else {
        await ebayAPI.resumeCampaign(campaign.campaign_id);
      }
      await loadCampaigns();
    } catch {}
  };

  if (loading) return <div className="text-center py-4 font-body text-xs text-neutral-400">Loading campaigns...</div>;

  return (
    <div className="space-y-3">
      {errorMsg && (
        <div className="p-2 bg-red-50 border-2 border-red-300 font-body text-xs text-red-700" data-testid="campaign-error">
          {errorMsg}
          <button onClick={() => setErrorMsg("")} className="ml-2 font-bold">×</button>
        </div>
      )}
      {/* Existing campaigns */}
      {campaigns.length > 0 ? (
        <div className="space-y-2">
          {/* Don't promote option */}
          <button
            onClick={() => handleSetDefault("")}
            className={`w-full p-2.5 border-2 text-left flex items-center justify-between ${!defaultId ? "border-black bg-black text-white" : "border-neutral-300 hover:border-black"}`}
            data-testid="campaign-dont-promote"
          >
            <span className="font-heading text-xs font-bold uppercase">Don't Promote</span>
            {!defaultId && <Star size={14} weight="fill" />}
          </button>

          {campaigns.map((c) => (
            <div key={c.campaign_id} className={`border-2 ${defaultId === c.campaign_id ? "border-black" : "border-neutral-300"}`}>
              {editingId === c.campaign_id ? (
                <div className="p-2.5 space-y-2">
                  <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)}
                    className="w-full p-1.5 border-2 border-black font-body text-sm" placeholder="Campaign name" />
                  <div className="flex items-center gap-2">
                    <input type="number" value={editRate} onChange={(e) => setEditRate(e.target.value)}
                      className="w-20 p-1.5 border-2 border-black font-body text-sm" min="1" max="50" step="0.5" />
                    <span className="font-body text-xs">% ad rate</span>
                    <div className="flex-1" />
                    <button onClick={() => handleUpdate(c.campaign_id)}
                      className="px-3 py-1.5 bg-black text-white font-heading text-xs font-bold uppercase">Save</button>
                    <button onClick={() => setEditingId(null)}
                      className="px-3 py-1.5 border border-black font-heading text-xs font-bold uppercase">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="p-2.5 flex items-center gap-2">
                  <button onClick={() => handleSetDefault(c.campaign_id)} className="flex-1 text-left min-w-0" data-testid={`campaign-${c.campaign_id}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-heading text-sm font-bold truncate">{c.name}</span>
                      <span className="font-body text-xs font-semibold text-neutral-500">{c.ad_rate}%</span>
                      {c.status === "PAUSED" && <span className="font-body text-[9px] font-bold uppercase text-amber-600 bg-amber-50 px-1">Paused</span>}
                      {defaultId === c.campaign_id && <Star size={12} weight="fill" className="text-amber-500 shrink-0" />}
                    </div>
                  </button>
                  <button onClick={() => handlePauseResume(c)} className="p-1 text-neutral-400 hover:text-black" title={c.status === "RUNNING" ? "Pause" : "Resume"}>
                    {c.status === "RUNNING" ? <Pause size={14} weight="bold" /> : <Play size={14} weight="bold" />}
                  </button>
                  <button onClick={() => { setEditingId(c.campaign_id); setEditName(c.name); setEditRate(String(c.ad_rate)); }}
                    className="p-1 text-neutral-400 hover:text-black"><PencilSimple size={14} weight="bold" /></button>
                  <button onClick={() => handleDelete(c.campaign_id)}
                    className="p-1 text-neutral-400 hover:text-red-600"><Trash size={14} weight="bold" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="border-2 border-dashed border-neutral-300 p-4 text-center">
          <Megaphone size={32} weight="bold" className="mx-auto text-neutral-300 mb-2" />
          <p className="font-body text-sm text-neutral-500 mb-3">No campaigns yet. Create your first or use our suggested starter set.</p>
          <button onClick={handleSuggest} disabled={creating}
            className="px-4 py-2 bg-black text-white font-heading text-xs font-bold uppercase border-2 border-black disabled:opacity-50"
            data-testid="suggest-campaigns-btn">
            {creating ? "Creating..." : "Create Starter Campaigns"}
          </button>
        </div>
      )}

      {/* Add new campaign */}
      <div className="border-2 border-dashed border-neutral-300 p-2.5">
        <div className="flex items-center gap-2">
          <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
            placeholder="Campaign name" className="flex-1 p-1.5 border-2 border-black font-body text-sm" data-testid="new-campaign-name" />
          <input type="number" value={newRate} onChange={(e) => setNewRate(e.target.value)}
            className="w-16 p-1.5 border-2 border-black font-body text-sm" min="1" max="50" step="0.5" data-testid="new-campaign-rate" />
          <span className="font-body text-xs">%</span>
          <button onClick={handleCreate} disabled={creating || !newName.trim()}
            className="p-1.5 bg-black text-white border-2 border-black disabled:opacity-50" data-testid="create-campaign-btn">
            <Plus size={14} weight="bold" />
          </button>
        </div>
      </div>

      <p className="font-body text-[10px] text-neutral-400">
        <Star size={10} weight="fill" className="inline text-amber-500" /> = default for new listings. Click a campaign to set as default.
      </p>
    </div>
  );
}

export function PolicySelect({ label, options, value, onChange, testid }) {
  return (
    <div>
      <label className="font-body text-xs font-semibold text-neutral-700">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full mt-1 p-2 border-2 border-black font-body text-sm bg-white"
        data-testid={testid}
      >
        <option value="">Auto-detect (first available)</option>
        {options.map((opt) => (
          <option key={opt.id} value={opt.id}>{opt.name}</option>
        ))}
      </select>
    </div>
  );
}

function HomeBaseInput({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [address, setAddress] = useState(value?.address || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!address.trim()) return;
    setSaving(true);
    await onSave({ address: address.trim() });
    setSaving(false);
    setEditing(false);
  };

  if (!editing && value?.address) {
    return (
      <div className="mt-1 flex items-center gap-2">
        <div className="flex-1 p-2 border-2 border-black bg-neutral-50 font-body text-sm">
          <div className="font-semibold">{value.formatted_address || value.address}</div>
          {value.lat && (
            <div className="text-[10px] text-neutral-400 mt-0.5">
              {value.lat.toFixed(4)}, {value.lng.toFixed(4)}
            </div>
          )}
        </div>
        <button
          onClick={() => { setEditing(true); setAddress(value.address || ""); }}
          className="p-2 border-2 border-black font-heading text-xs font-bold uppercase hover:bg-neutral-100"
          data-testid="edit-home-base-btn"
        >
          Edit
        </button>
      </div>
    );
  }

  return (
    <div className="mt-1 flex items-center gap-2">
      <input
        type="text"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && handleSave()}
        placeholder="e.g. 123 Main St, City, State ZIP"
        className="flex-1 p-2 border-2 border-black font-body text-sm"
        autoFocus={editing}
        data-testid="home-base-input"
      />
      <button
        onClick={handleSave}
        disabled={saving || !address.trim()}
        className="p-2 bg-black text-white border-2 border-black font-heading text-xs font-bold uppercase disabled:opacity-50"
        data-testid="save-home-base-btn"
      >
        {saving ? "..." : "Save"}
      </button>
      {editing && (
        <button
          onClick={() => setEditing(false)}
          className="p-2 border-2 border-black font-heading text-xs font-bold uppercase"
        >
          Cancel
        </button>
      )}
    </div>
  );
}
